import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { promoCodeAPI } from '../../services/api';

const PromoCodeUsageDialog = ({ open, onClose, promoCode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  useEffect(() => {
    if (open && promoCode) {
      fetchUsageHistory();
      fetchPromoCodeDetails();
    }
  }, [open, promoCode, page]);
  
  const fetchUsageHistory = async () => {
    if (!promoCode) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await promoCodeAPI.getPromoCodeUsage(promoCode.id, { page, limit });
      
      if (response.success) {
        setUsageHistory(response.data);
        setTotal(response.pagination.total);
      } else {
        setError(response.error || 'Failed to fetch usage history');
      }
    } catch (err) {
      console.error('Error fetching usage history:', err);
      setError('Failed to fetch usage history');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPromoCodeDetails = async () => {
    if (!promoCode) return;
    
    try {
      const response = await promoCodeAPI.getPromoCode(promoCode.id);
      
      if (response.success && response.data.usageStats) {
        setUsageStats(response.data.usageStats);
      }
    } catch (err) {
      console.error('Error fetching promo code details:', err);
    }
  };
  
  const handleExport = () => {
    // Create CSV content
    const headers = ['Customer Name', 'Customer Email', 'Order Number', 'Date Used', 'Original Amount', 'Discount', 'Final Amount'];
    const rows = usageHistory.map(u => [
      u.userName,
      u.userEmail,
      u.orderNumber,
      new Date(u.usedAt).toLocaleDateString(),
      `$${u.originalAmount.toFixed(2)}`,
      `$${u.discountAmount.toFixed(2)}`,
      `$${u.finalAmount.toFixed(2)}`
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo_code_${promoCode?.code}_usage.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  if (!promoCode) return null;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Promo Code Usage: {promoCode.code}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Summary Stats */}
        {usageStats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {usageStats.totalUses || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Uses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {usageStats.uniqueCustomers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Customers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                    ${(usageStats.totalDiscountGiven || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Discount Given
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        
        {/* Usage Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Order</strong></TableCell>
                <TableCell><strong>Date Used</strong></TableCell>
                <TableCell align="right"><strong>Original</strong></TableCell>
                <TableCell align="right"><strong>Discount</strong></TableCell>
                <TableCell align="right"><strong>Final</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : usageHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No usage history found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                usageHistory.map((usage) => (
                  <TableRow key={usage.id} hover>
                    <TableCell>
                      <Typography variant="body2">{usage.userName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {usage.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{usage.orderNumber}</TableCell>
                    <TableCell>
                      {new Date(usage.usedAt).toLocaleDateString()} {new Date(usage.usedAt).toLocaleTimeString()}
                    </TableCell>
                    <TableCell align="right">${usage.originalAmount.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                      -${usage.discountAmount.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">${usage.finalAmount.toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {total > limit && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Page {page} of {Math.ceil(total / limit)}
            </Typography>
            <Button
              disabled={page >= Math.ceil(total / limit)}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button 
          startIcon={<DownloadIcon />} 
          onClick={handleExport}
          disabled={usageHistory.length === 0}
        >
          Export CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoCodeUsageDialog;

